import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Wand2 } from 'lucide-react';
import { api, type TxRow, type BulkAssignBody } from '../lib/api';
import { Pagination } from '../components/Pagination';
import { AssignDropdown } from '../components/AssignDropdown';
import { EmptyState } from '../components/ui/empty-state';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { BulkActionBar } from '../components/BulkActionBar';
import { OperationResultStack, type OperationResult } from '../components/OperationResultStack';
import { TransactionsFilters } from '../components/TransactionsFilters';
import { ConfirmDialog, type ConfirmDialogProps } from '../components/ConfirmDialog';
import { useRowSelection } from '../hooks/useRowSelection';
import { formatVnd, formatDateTime, formatAmountOriginal } from '../lib/format';
import { PageHeader } from '@/components/layout/page-header';

function PendingRow({
  tx,
  selected,
  onToggle,
}: {
  tx: TxRow;
  selected: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const { data: sug } = useQuery({
    queryKey: ['suggestions', tx.id],
    queryFn: () => api.getSuggestions(tx.id),
  });
  const assign = useMutation({
    mutationFn: (department_id: number) => api.assignTransaction(tx.id, department_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['txs'] });
    },
  });
  return (
    <div
      className={`rounded-lg border bg-card p-4 transition-colors ${
        selected ? 'border-primary/50' : 'border-border'
      }`}
    >
      <div className="mb-2 flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-0.5 h-5 w-5 cursor-pointer accent-primary"
          aria-label={`Chọn giao dịch #${tx.id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
            <span className="text-xs text-muted-foreground">
              {formatDateTime(tx.occurred_at)} · {tx.card_alias}
            </span>
            <span className="font-mono tabular-nums text-foreground">
              {formatAmountOriginal(tx.amount_original, tx.currency)}
              {tx.currency !== 'VND' && (
                <span className="ml-1 text-muted-foreground">· {formatVnd(tx.amount_vnd)}</span>
              )}
            </span>
          </div>
          <div className="mb-3 text-sm">
            <div className="truncate font-medium text-foreground">
              {tx.merchant_canonical ?? tx.raw_merchant_text}
            </div>
            {tx.merchant_canonical && tx.merchant_canonical !== tx.raw_merchant_text && (
              <div className="truncate text-xs text-muted-foreground/70">
                {tx.raw_merchant_text}
              </div>
            )}
          </div>
          <AssignDropdown
            suggestions={sug?.suggestions ?? []}
            onAssign={(dept_id) => assign.mutate(dept_id)}
            disabled={assign.isPending}
          />
        </div>
      </div>
    </div>
  );
}

export function PendingPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const page = Math.max(1, parseInt(params.get('page') ?? '1', 10));
  const filterParams = {
    page,
    card_id: params.get('card') ? parseInt(params.get('card') as string, 10) : undefined,
    date_from: params.get('date_from') || undefined,
    date_to: params.get('date_to') || undefined,
    type: (params.get('type') || undefined) as
      | 'purchase'
      | 'refund'
      | 'fee'
      | 'decline'
      | undefined,
    q: params.get('q') || undefined,
  };
  const { data, isLoading } = useQuery({
    queryKey: ['pending', filterParams],
    queryFn: () => api.listPending(filterParams),
  });
  const rows = data?.rows ?? [];
  const totalMatching = data?.total_count ?? 0;
  const resetKey = `${page}|${params.toString()}`;
  const { selectedIds, allOnPage, toggle, toggleAllOnPage, clear } = useRowSelection(
    rows,
    resetKey,
  );
  const [filterMode, setFilterMode] = useState(false);
  const [opResults, setOpResults] = useState<OperationResult[]>([]);
  const [bulkDept, setBulkDept] = useState<string>('');
  const [confirm, setConfirm] = useState<Omit<ConfirmDialogProps, 'open' | 'onCancel'> | null>(
    null,
  );

  const { data: deptsData } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => api.listDepartments(),
    staleTime: 5 * 60_000,
  });

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () =>
      fetch('/api/auth/me', { credentials: 'include' }).then(
        (r) => r.json() as Promise<{ user: { role: string } }>,
      ),
    staleTime: 5 * 60_000,
  });
  const isAdmin = meData?.user?.role === 'admin';

  const autoAssign = useMutation({
    mutationFn: () => api.autoAssignTransactions(),
    onSuccess: (res) => {
      setOpResults((p) =>
        [
          {
            key: `auto-assign-${Date.now()}`,
            kind: 'success' as const,
            title: `Auto-assign: ${res.assigned} transaction`,
          },
          ...p,
        ].slice(0, 5),
      );
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['txs'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (e) => {
      const msg = String(e);
      const isTooMany = msg.includes('too_many');
      setOpResults((p) =>
        [
          {
            key: `auto-assign-err-${Date.now()}`,
            kind: 'failure' as const,
            title: isTooMany
              ? 'Quá nhiều tx pending — dùng filter + bulk-assign'
              : 'Auto-assign fail',
            detail: msg.slice(0, 200),
          },
          ...p,
        ].slice(0, 5),
      );
      setConfirm(null);
    },
  });

  function openAutoAssignConfirm() {
    setConfirm({
      title: 'Auto-assign pending transactions?',
      description:
        'Hệ thống sẽ assign mọi tx pending có card đã set owning department. Tx của card chưa có owner sẽ giữ nguyên unassigned.',
      confirmLabel: 'Run',
      tone: 'primary',
      pending: autoAssign.isPending,
      onConfirm: () => autoAssign.mutate(),
    });
  }

  const bulkAssign = useMutation({
    mutationFn: (body: BulkAssignBody) => api.bulkAssignTransactions(body),
    onSuccess: (res) => {
      setOpResults((p) =>
        [
          {
            key: `bulk-assign-${Date.now()}`,
            kind: 'success' as const,
            title: `Đã assign ${res.affected} transaction`,
          },
          ...p,
        ].slice(0, 5),
      );
      clear();
      setFilterMode(false);
      setBulkDept('');
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ['pending'] });
      qc.invalidateQueries({ queryKey: ['txs'] });
      qc.invalidateQueries({ queryKey: ['kpis'] });
    },
    onError: (e) => {
      setOpResults((p) =>
        [
          {
            key: `bulk-assign-err-${Date.now()}`,
            kind: 'failure' as const,
            title: 'Bulk assign fail',
            detail: String(e).slice(0, 200),
          },
          ...p,
        ].slice(0, 5),
      );
    },
  });

  function applyBulk() {
    const dept = Number(bulkDept);
    if (!dept) return;
    const deptCode = deptsData?.rows.find((d) => d.id === dept)?.code ?? `#${dept}`;
    const count = filterMode ? totalMatching : selectedIds.size;
    setConfirm({
      title: `Assign ${count} giao dịch sang ${deptCode}?`,
      description: `Sau khi assign, ${count} giao dịch sẽ thuộc bộ phận "${deptCode}". Có thể chỉnh sửa từng giao dịch sau.`,
      confirmLabel: 'Assign',
      tone: 'primary',
      pending: bulkAssign.isPending,
      onConfirm: () => {
        if (filterMode) {
          bulkAssign.mutate({
            mode: 'filter',
            filter: { department: 'unassigned' },
            department_id: dept,
          });
        } else {
          bulkAssign.mutate({
            mode: 'ids',
            ids: [...selectedIds],
            department_id: dept,
          });
        }
      },
    });
  }

  return (
    <div className="space-y-4 pb-24">
      <PageHeader
        title="Pending queue"
        subtitle={data ? `${totalMatching} unassigned` : undefined}
        actions={
          isAdmin ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={openAutoAssignConfirm}
              disabled={autoAssign.isPending}
            >
              <Wand2 size={14} strokeWidth={1.75} />
              {autoAssign.isPending ? 'Đang…' : 'Auto Assign'}
            </Button>
          ) : null
        }
      />
      <OperationResultStack
        items={opResults}
        onDismiss={(k) => setOpResults((p) => p.filter((r) => r.key !== k))}
      />
      <TransactionsFilters omit={['department', 'has_alert']} />
      {isLoading && <Skeleton className="h-40 w-full" />}
      {data && rows.length === 0 && (
        <EmptyState
          icon={CheckCircle2}
          title="No pending transactions"
          description="Không có giao dịch chờ gán. 🎉"
        />
      )}
      {data && rows.length > 0 && (
        <>
          <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={allOnPage}
              onChange={toggleAllOnPage}
              className="h-5 w-5 cursor-pointer accent-primary"
              aria-label="Chọn tất cả trang"
            />
            <span className="text-muted-foreground">Chọn tất cả trang ({rows.length} dòng)</span>
          </div>
          <div className="space-y-3">
            {rows.map((tx) => (
              <PendingRow
                key={tx.id}
                tx={tx}
                selected={selectedIds.has(tx.id)}
                onToggle={() => toggle(tx.id)}
              />
            ))}
          </div>
          <Pagination page={page} totalPages={data.total_pages} />
        </>
      )}

      <BulkActionBar
        selectedCount={selectedIds.size}
        totalMatching={totalMatching}
        isAllOnPageSelected={allOnPage}
        filterMode={filterMode}
        onEscalateToFilter={() => setFilterMode(true)}
        onClear={() => {
          clear();
          setFilterMode(false);
        }}
      >
        <select
          value={bulkDept}
          onChange={(e) => setBulkDept(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Chọn dept…</option>
          {(deptsData?.rows ?? [])
            .filter((d) => !d.deleted_at)
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.code}
              </option>
            ))}
        </select>
        <Button
          size="sm"
          variant="default"
          onClick={applyBulk}
          disabled={!bulkDept || bulkAssign.isPending}
        >
          {bulkAssign.isPending ? 'Đang…' : 'Assign'}
        </Button>
      </BulkActionBar>

      <ConfirmDialog
        open={confirm != null}
        title={confirm?.title ?? ''}
        description={confirm?.description ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'OK'}
        tone={confirm?.tone}
        pending={confirm?.pending}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
